<script lang="ts">
  import { onMount } from "svelte";
  import { referralCodeStore } from "@shared/svelte/Stores/ReferralCodeStore";
  import moment from "moment";
  import _ from "lodash";
  import Flash from "@shared/services/flashv2.js";
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";
  import { DistributorStore } from "@distributor/svelte/Stores/DistributorStore";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import PageWrapper from "@retailer/svelte/Shared/PageWrapper.svelte";
  import FormErrors from "@shared/svelte/Components/FormErrors.svelte";
  import PhoneInput from "@shared/svelte/Components/PhoneInput.svelte";
  import Pagination from "@shared/svelte/Components/Pagination/Small.svelte";

  import type { ReferralLead } from "@shared/types/ReferralLead";
  import type { DistributorUser } from "@shared/types/Distributor";

  export let stateParams;
  export let state;
  export let referralLeadsRes;
  export let referralCodeRes;

  let user: any = $CurrentUserStore;
  let currentDistributor: DistributorUser | null = $DistributorStore.currentDistributor;
  let appName = window["C"].APP_NAME;
  let referralCode = referralCodeRes.data.code;
  let referralLeads: ReferralLead[] = referralLeadsRes.data || [];
  let submittingData = false;
  let newReferralLead: ReferralLead = {
    name: "",
    sent_to_email: "",
    sent_to_phone: "",
  };
  let inviteErrors = null;
  let newReferralReward = appName === "retailer";
  let hideReferralIncentive =
    appName === "distributor" && currentDistributor?.hide_referral_incentive;

  const refreshState = changes => {
    const newParams = { ...stateParams };
    _.extend(newParams, changes);
    state.go("layout.referrals", newParams);
  };

  let pagination = {
    perPage: parseInt(referralLeadsRes.headers("X-Pagination-Limit")),
    page: Number(stateParams.page || 1),
    totalEntries: parseInt(referralLeadsRes.headers("X-Pagination-Entries")),
    goToPage: function (n) {
      refreshState({ page: n });
    },
  };

  let totalEarnings = 0;

  const getTotalEarnings = async () => {
    const options = {
      method: "GET",
      url: `/api/${appName}/referral_code/earnings`,
    };

    const res = await HttpPromise(options);
    totalEarnings = Number(res.total) || 0;
  };

  /*
   * Update User
   */

  const updateDistributorUser = () => {
    if (appName !== "distributor") {
      return;
    }
    referralCodeStore.set({
      code: referralCode,
    });
  };

  /*
   * Sending New Invites
   */

  const sendInvite = () => {
    if (submittingData) {
      return;
    }
    submittingData = true;

    HttpPromise({
      method: "POST",
      url: `/api/${appName}/referral_leads`,
      data: {
        name: newReferralLead.name,
        sent_to_email: newReferralLead.sent_to_email,
        sent_to_phone: newReferralLead.sent_to_phone,
      },
    })
      .then(
        function success(res) {
          referralLeads = [{ ...res }, ...referralLeads];
          newReferralLead = {
            name: "",
            sent_to_email: "",
            sent_to_phone: "",
          };
          Flash.now({
            type: "info",
            message: "Invite Sent",
          });
        },
        function errors(res) {
          if (res && res.errors) {
            inviteErrors = res.errors;
          }
        },
      )
      .finally(function () {
        submittingData = false;
      });
  };

  /*
   * Resending Invites
   */

  const resendInvite = referralLeadId => {
    if (submittingData) {
      return;
    }
    submittingData = true;

    HttpPromise({
      method: "PUT",
      url: `/api/${appName}/referral_leads/${referralLeadId}/resend`,
    })
      .then(function success(res) {
        const leadsCopy = [...referralLeads];
        const referralLead = _.find(leadsCopy, ["id", referralLeadId]);
        _.extend(referralLead, res);
        referralLeads = leadsCopy;
        Flash.now({
          type: "info",
          message: "Invite Resent",
        });
      })
      .finally(function () {
        submittingData = false;
      });
  };

  const shouldShowInvite = referralLead => {
    if (!referralLead.sent_at) {
      return false;
    }

    if (referralLead.state === "opened") {
      return true;
    }
    if (referralLead.state === "sent") {
      // Do not show until after 24 hours have passed
      // since last sending the invite
      const isAfter24Hours = moment().diff(referralLead.sent_at, "m") > 1440;
      const resentLimit = appName === "retailer" ? 2 : 25;
      return isAfter24Hours && referralLead.resends < resentLimit;
    }

    return false;
  };

  const contactInfo = referralLead => {
    const info = [referralLead.sent_to_email, referralLead.sent_to_phone];
    return _.compact(info).join(", ");
  };

  const referralLeadSentAt = referralLead => {
    if (referralLead.sent_at) {
      return referralLead.sent_at ? moment(referralLead.sent_at).fromNow() : "Today";
    } else {
      return referralLead.signed_up_at ? moment(referralLead.signed_up_at).fromNow() : "Today";
    }
  };

  const referralLeadStatus = referralLead => {
    const statusKey = {
      sent: "Sent",
      opened: "Delivered",
      signed_up: "Signed up",
      placed_first_order: "First order placed!",
    };
    return _.get(statusKey, referralLead.state, "Sending");
  };

  onMount(() => {
    getTotalEarnings();
    updateDistributorUser();
  });
</script>

<PageWrapper let:mobileMode useBackgroundWhite={false}>
  <div class="background-white">
    <div class="grid-container">
      <div class="grid-row align--middle padding-top--smaller padding-bottom--smaller">
        <div class="grid-col c12-12">
          <span class="default-subheader--bold">Refer &amp; Earn</span>
        </div>
      </div>
    </div>
  </div>

  <div class="refer-and-earn-header align-center background-green">
    <img
      class="display-inline-block margin-left--small margin-right--small"
      src={window["C"].IMAGE_PATHS["referrals/refer-and-earn-icon.png"]}
      alt="Refer and Earn Icon"
      width="50px"
      height="50px"
    />
    {#if mobileMode}
      <br />
    {/if}
    <h1 class="display-inline-block s2 color-white position-relative margin-top--small">
      Your Refer &amp; Earn Code: <span class="strong uppercase">{referralCode}</span>
    </h1>
  </div>

  <div class="refer-and-earn-wrapper background-white position-relative">
    <div class="cart__item-header padding--medium">
      <div class="align-center">
        <h1 class="default-headline--regular padding-bottom--smaller">Invite to Join Provi</h1>
        {#if !hideReferralIncentive}
          <div class="s3 color-grayDark" data-testid="referral-incentive-amount">
            and earn a
            <span class="strong color-green">
              {#if newReferralReward}
                $100 Mastercard gift card
              {:else}
                $25 gift card
              {/if}
            </span> when they place their first order!
          </div>
        {/if}
      </div>
      <div class="refer-and-earn-form">
        <FormErrors errors={inviteErrors} />
        <div class="grid-row">
          <div class="grid-col c12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              Name
            </div>
            <div class="input-wrapper full-width">
              <input
                type="text"
                class="input"
                name="New Referral Name"
                placeholder="Your Friend's Name"
                bind:value={newReferralLead.name}
                disabled={submittingData}
                required
              />
            </div>
          </div>
        </div>

        <div class="grid-row">
          <div class="grid-col c6-12--md c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              Send Email Invite
            </div>
            <div class="input-wrapper full-width">
              <input
                type="text"
                class="input"
                name="New Referral Email"
                placeholder="Your Friend's Email"
                bind:value={newReferralLead.sent_to_email}
                disabled={submittingData}
                required={!newReferralLead.sent_to_phone}
              />
            </div>
          </div>
          <div class="grid-col c6-12--md c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              Send Text Message Invite
            </div>
            <div class="input-wrapper full-width">
              <PhoneInput
                extraClasses="input phone-input"
                placeholder="Your Friend's Mobile Phone"
                name="New Referral Phone"
                bind:value={newReferralLead.sent_to_phone}
                disabled={submittingData}
                required={!newReferralLead.sent_to_phone}
              />
            </div>
          </div>
        </div>

        <div class="grid-row padding-top--small">
          <div class="grid-col c12-12 align-center">
            <button
              class="button button--primary button--large full-width padding--small"
              disabled={submittingData ||
                (!newReferralLead.sent_to_email.length &&
                  newReferralLead.sent_to_phone.length < 10)}
              on:click|preventDefault={sendInvite}
            >
              <span class:s3={!mobileMode}>Send Invite</span>
            </button>
          </div>
        </div>

        <div class="grid-row padding-top--small margin-top--smallest">
          <div class="grid-col c12-12 align-center">
            <div class={mobileMode ? "s4 color-grayDark" : "s3 color-grayDark"}>
              Or share this link:
              <a
                class="color-brandPrimary underline"
                href={`https://app.provi.com/invite/${referralCode.toUpperCase()}`}
                target="_blank"
                rel="noopener noreferrer"
                class:display-block={mobileMode}
              >
                {`app.provi.com/invite/${referralCode.toUpperCase()}`}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="padding--medium">
      <div class="grid-row margin-bottom--small">
        <div class="grid-col c6-12"><div class="s3">Invitations</div></div>
        {#if !hideReferralIncentive}
          <div class="grid-col c6-12" data-testid="referral-incentive-earnings">
            <div class="s3 float-right">
              Total Earnings: <span class="strong color-green">${totalEarnings}</span>
            </div>
          </div>
        {/if}
      </div>

      <div class="grid-row padding--smaller">
        {#if !mobileMode}
          <div class="grid-col c2-12 color-black opacity-60">
            <div class="s5">Invite Sent</div>
          </div>
        {/if}
        <div class="grid-col c8-12 color-black opacity-60">
          <div class={mobileMode ? "s6" : "s5"}>Retailer</div>
        </div>
        <div
          class={mobileMode
            ? "grid-col color-black opacity-60 align-right c4-12"
            : "grid-col color-black opacity-60 align-right c2-12"}
        >
          <div class={mobileMode ? "s6" : "s5"}>Invite Status</div>
        </div>
      </div>

      <div id="referral_leads" class="tile">
        {#if referralLeads.length}
          {#each referralLeads as referralLead}
            <div class="tile__row" id={`referral_lead_${referralLead.id}`}>
              <div class="grid-row padding--smaller">
                {#if !mobileMode}
                  <div class="grid-col c2-12 color-black align-left">
                    <div class="s5">{referralLeadSentAt(referralLead)}</div>
                  </div>
                {/if}

                <div class="grid-col c8-12 color-black align-left">
                  <div class="grid-row">
                    <div class="grid-col c12-12 color-black">
                      <div class={mobileMode ? "s6" : "s5"}>{referralLead.name}</div>
                    </div>
                  </div>

                  <div class="grid-row">
                    <div class="grid-col c12-12 color-black">
                      <div class="s6 color-grayDark">{contactInfo(referralLead)}</div>
                    </div>
                  </div>
                </div>

                <div
                  class={mobileMode ? "grid-col align-right c4-12" : "grid-col align-right c2-12"}
                >
                  {#if shouldShowInvite(referralLead)}
                    <button
                      class="button button--label button--secondary"
                      disabled={submittingData}
                      on:click|preventDefault={() => resendInvite(referralLead.id)}
                    >
                      Resend Invite
                    </button>
                  {/if}
                  <div class={mobileMode ? "strong s6" : "strong s5"}>
                    {referralLeadStatus(referralLead)}
                  </div>
                </div>
              </div>
            </div>
          {/each}
        {:else}
          <div class="tile__row">
            <div class="grid-row padding--smaller">
              <div class="grid-col c12-12 align-center opacity-60">No Invitations Sent</div>
            </div>
          </div>
        {/if}
      </div>

      {#if referralLeads.length}
        <div class="fizz-styles">
          <div class="fizz-flex-row fizz-justify-end padding-top--small">
            <Pagination {pagination} removeSpacing />
          </div>
        </div>
      {/if}
    </div>
  </div>
</PageWrapper>
